import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const grantKeys = {
    all: ['grants'],
    proposals: (orgId) => [...grantKeys.all, 'proposals', orgId],
    proposal: (id) => [...grantKeys.all, 'proposal', id],
    drafts: (orgId) => [...grantKeys.all, 'drafts', orgId],
    internalReview: (orgId) => [...grantKeys.all, 'internal-review', orgId],
    submitted: (orgId) => [...grantKeys.all, 'submitted', orgId],
    awarded: (orgId) => [...grantKeys.all, 'awarded', orgId],
    rejected: (orgId) => [...grantKeys.all, 'rejected', orgId],
    summary: (orgId) => [...grantKeys.all, 'summary', orgId],
    history: (id) => [...grantKeys.all, 'history', id],
};

export function useGrantProposals(orgId) {
    return useQuery({
        queryKey: grantKeys.proposals(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantProposal(id, orgId) {
    return useQuery({
        queryKey: grantKeys.proposal(id),
        queryFn: async () => {
            return apiHelper.getResource(`/gnt/proposals/${id}/`, { orgId });
        },
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useGrantDrafts(orgId) {
    return useQuery({
        queryKey: grantKeys.drafts(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/drafts/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantInternalReview(orgId) {
    return useQuery({
        queryKey: grantKeys.internalReview(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/internal-review/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantSubmitted(orgId) {
    return useQuery({
        queryKey: grantKeys.submitted(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/submitted/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantAwarded(orgId) {
    return useQuery({
        queryKey: grantKeys.awarded(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/awarded/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantRejected(orgId) {
    return useQuery({
        queryKey: grantKeys.rejected(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/rejected/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantSummary(orgId) {
    return useQuery({
        queryKey: grantKeys.summary(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/gnt/proposals/summary/', { orgId });
        },
        enabled: !!orgId,
        retry: false,
    });
}

export function useGrantHistory(id, orgId) {
    return useQuery({
        queryKey: grantKeys.history(id),
        queryFn: async () => {
            return apiHelper.getResource(`/gnt/proposals/${id}/history/`, { orgId });
        },
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useCreateGrantProposal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/gnt/proposals/', data, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.drafts(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.summary(orgId) });
        },
    });
}

export function useUpdateGrantProposal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.patchResource(`/gnt/proposals/${id}/`, data, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposal(id) });
            queryClient.invalidateQueries({ queryKey: grantKeys.drafts(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.summary(orgId) });
        },
    });
}

export function useDeleteGrantProposal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.deleteResource(`/gnt/proposals/${id}/`, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposals(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.drafts(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.internalReview(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.submitted(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.awarded(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.rejected(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.summary(orgId) });
        },
    });
}

export function useDownloadGrantAttachment() {
    return useMutation({
        mutationFn: ({ proposalId, attachmentId, filename, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.downloadResource(
                `/gnt/proposals/${proposalId}/attachments/${attachmentId}/download/`,
                filename,
                options
            );
        },
    });
}

export function useReplaceGrantAttachment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ proposalId, attachmentId, file, orgId }) => {
            const formData = new FormData();
            formData.append('file', file);
            const options = orgId ? { orgId } : {};
            return apiHelper.patchResource(
                `/gnt/proposals/${proposalId}/attachments/${attachmentId}/replace/`,
                formData,
                options
            );
        },
        onSuccess: (_, { proposalId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposal(proposalId) });
        },
    });
}

export function useUploadGrantAttachments() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, files, orgId }) => {
            const formData = new FormData();
            files.forEach(f => formData.append('files', f.file));
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/gnt/proposals/${id}/upload-attachments/`, formData, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposal(id) });
            queryClient.invalidateQueries({ queryKey: grantKeys.drafts(orgId) });
        },
    });
}

export function useDeleteGrantAttachment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ proposalId, attachmentId, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.deleteResource(`/gnt/proposals/${proposalId}/delete-attachment/`, {
                ...options,
                data: { attachment_id: attachmentId },
            });
        },
        onSuccess: (_, { proposalId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposal(proposalId) });
        },
    });
}

export function useTransitionGrantProposal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/gnt/proposals/${id}/transition/`, data, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: grantKeys.proposal(id) });
            queryClient.invalidateQueries({ queryKey: grantKeys.proposals(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.drafts(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.internalReview(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.submitted(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.awarded(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.rejected(orgId) });
            queryClient.invalidateQueries({ queryKey: grantKeys.summary(orgId) });
        },
    });
}
